/**
 * Accounting Dashboards tile registry — the GWI360 port of V1's Accounting Dashboard
 * (cms-dev/dashboard_accounting.pl). Phase 1 ships every V1 block as a placeholder tile so
 * the card keys and default arrangement are fixed now; each later phase wires one tile's
 * data. Unrelated to LC Dashboards' "Accounting" workspace (different numbers, different
 * team) — nothing here reads /lc-dashboards.
 */
import type { CountPanelConfig } from '../../components/CountPanel';
import type { PanelDef, PanelLayoutItem, PanelLayouts } from '../../components/panelGrid/panelGridTypes';

export const ACCT_DASHBOARD_TYPE = 'accounting';
export const ACCT_PANELS_SETTING_KEY = 'accounting_dashboard_panels';
export const ACCT_WORKSPACE_ID = 'default';

// V1 colors: red = needs attention, orange = in progress/researching, green = ready/good.
// CountPanel only tints a chip when its count is > 0, so these are inert on the placeholders.
export const ACCT_COUNT_PANELS: CountPanelConfig[] = [
  {
    key: 'acct-cms',
    title: 'CMS',
    showAmounts: true,
    columns: [{
      header: { key: 'acct-cms-total', label: 'CMS' },
      rows: [
        { key: 'acct-cms-not-invoiced', label: 'Not Invoiced' },
        { key: 'acct-cms-delivered', label: 'Delivered', indent: 1 },
        { key: 'acct-cms-delivered-inv-gt0', label: 'Inv Amt > $0', indent: 2 },
        { key: 'acct-cms-delivered-inv-eq0', label: 'Inv Amt = $0', indent: 2, color: 'red' },
        { key: 'acct-cms-not-delivered', label: 'Not Delivered', indent: 1 },
        { key: 'acct-cms-not-delivered-inv-gt0', label: 'Inv Amt > $0', indent: 2 },
        { key: 'acct-cms-not-delivered-inv-eq0', label: 'Inv Amt = $0', indent: 2, color: 'red' },
        { key: 'acct-cms-invoiced', label: 'Invoiced' },
      ],
    }],
  },
  {
    key: 'acct-accessorials',
    title: 'Accessorials',
    showAmounts: true,
    columns: [{
      header: { key: 'acct-acc-total', label: 'Accessorials' },
      rows: [
        { key: 'acct-acc-not-invoiced', label: 'Not Invoiced' },
        { key: 'acct-acc-not-invoiced-per-diem', label: 'Per Diem', indent: 1 },
        { key: 'acct-acc-delivered', label: 'Delivered', indent: 1 },
        { key: 'acct-acc-delivered-inv-gt0', label: 'Inv Amt > $0', indent: 2 },
        { key: 'acct-acc-delivered-inv-eq0', label: 'Inv Amt = $0', indent: 2, color: 'red' },
        { key: 'acct-acc-not-delivered', label: 'Not Delivered', indent: 1 },
        { key: 'acct-acc-not-delivered-inv-gt0', label: 'Inv Amt > $0', indent: 2 },
        { key: 'acct-acc-not-delivered-inv-eq0', label: 'Inv Amt = $0', indent: 2, color: 'red' },
        { key: 'acct-acc-invoiced', label: 'Invoiced' },
        { key: 'acct-acc-invoiced-per-diem', label: 'Per Diem', indent: 1 },
      ],
    }],
  },
  {
    key: 'acct-pending-invoicing',
    title: 'Pending Invoicing',
    columns: [{
      header: { key: 'acct-pinv-total', label: 'Pending Invoicing' },
      rows: [
        { key: 'acct-pinv-imports', label: 'Imports', color: 'green' },
        { key: 'acct-pinv-exports', label: 'Exports', color: 'green' },
        { key: 'acct-pinv-warehouse', label: 'Warehouse', color: 'green' },
        { key: 'acct-pinv-warehouse-storage', label: 'Warehouse Storage', color: 'green' },
        { key: 'acct-pinv-edi', label: 'EDI', color: 'green' },
        { key: 'acct-pinv-accessorials', label: 'Accessorials', color: 'green' },
        { key: 'acct-pinv-per-diem', label: 'Per Diem', color: 'green' },
        { key: 'acct-pinv-chassis', label: 'Chassis', color: 'green' },
      ],
    }],
  },
  {
    key: 'acct-invoicing-hold',
    title: 'Invoicing Hold',
    columns: [{
      header: { key: 'acct-hold-total', label: 'Invoicing Hold' },
      rows: [
        { key: 'acct-hold-revised-do', label: 'Invoice Hold', color: 'red' },
        { key: 'acct-hold-pod-docs', label: 'POD/BOL/MR/PL/EIR', color: 'red' },
        { key: 'acct-hold-edi', label: 'EDI', color: 'red' },
        { key: 'acct-hold-pending-approval', label: 'Pending Approval', color: 'red' },
        { key: 'acct-hold-margin', label: 'Margin', color: 'red' },
        { key: 'acct-hold-trouble-ticket', label: 'Trouble Ticket', color: 'red' },
        { key: 'acct-hold-missing-driver-pay-rate', label: 'Missing Driver Pay Rate', color: 'red' },
      ],
    }],
  },
  {
    // Prefix "acct-acchold-" (not "acct-acc-hold-") so no key of this tile is a strict
    // prefix-extension of the Accessorials tile's "acct-acc-*" keys.
    key: 'acct-accessorials-on-hold',
    title: 'Accessorials On Hold',
    columns: [{
      header: { key: 'acct-acchold-total', label: 'Accessorials On Hold' },
      rows: [
        { key: 'acct-acchold-revised-do', label: 'Invoice Hold', color: 'red' },
        { key: 'acct-acchold-pod-docs', label: 'POD/BOL/MR/PL/EIR', color: 'red' },
        { key: 'acct-acchold-edi', label: 'EDI', color: 'red' },
        { key: 'acct-acchold-pending-approval', label: 'Pending Approval', color: 'red' },
        { key: 'acct-acchold-margin', label: 'Margin', color: 'red' },
        { key: 'acct-acchold-trouble-ticket', label: 'Trouble Ticket', color: 'red' },
        { key: 'acct-acchold-missing-driver-pay-rate', label: 'Missing Driver Pay Rate', color: 'red' },
      ],
    }],
  },
  {
    key: 'acct-approval',
    title: 'Approval',
    columns: [{
      header: { key: 'acct-approval-total', label: 'Approval' },
      rows: [
        { key: 'acct-approval-orders-not-approved', label: 'Orders Not Approved', color: 'red' },
        { key: 'acct-approval-templates-not-approved', label: 'Templates Not Approved', color: 'red' },
        { key: 'acct-approval-donotbill', label: 'DO NOT BILL', headerStyle: true },
        { key: 'acct-approval-donotbill-not-approved', label: 'Not Approved', indent: 1, color: 'red' },
        { key: 'acct-approval-donotbill-researching', label: 'Researching', indent: 1, color: 'orange' },
        { key: 'acct-approval-acc-donotbill', label: 'Accessorials - DO NOT BILL', headerStyle: true },
        { key: 'acct-approval-acc-donotbill-not-approved', label: 'Not Approved', indent: 1, color: 'red' },
        { key: 'acct-approval-acc-donotbill-researching', label: 'Researching', indent: 1, color: 'orange' },
      ],
    }],
  },
  {
    // V1's "Accounting" box has no total of its own; the tile title names it.
    key: 'acct-accounting',
    title: 'Accounting',
    columns: [{
      rows: [
        { key: 'acct-past-due-invoicing', label: '15 Days Past Due Invoicing', color: 'red' },
        { key: 'acct-past-due-accessorial', label: '15 Days Past Due Accessorial', color: 'red' },
      ],
    }],
  },
  {
    key: 'acct-accounts-payable',
    title: 'Accounts Payable',
    columns: [{
      // V1 reads these hold-type rows from cms.additionalcost_holdtypes (tablemaint-editable);
      // the port pins the 7 active types as of 2026-09-09. The card phase should decide
      // whether to derive them from the hold-types endpoint.
      rows: [
        { key: 'acct-ap-pending-approval', label: 'Pending Approval', color: 'green' },
        { key: 'acct-ap-on-hold', label: 'On Hold', color: 'red' },
        { key: 'acct-ap-review', label: 'Review', indent: 1, color: 'red' },
        { key: 'acct-ap-pending-invoicing', label: 'Pending Invoicing', indent: 1, color: 'red' },
        { key: 'acct-ap-pending-responsibility', label: 'Pending Responsibility', indent: 1, color: 'red' },
        { key: 'acct-ap-vendor-dispute', label: 'Vendor Dispute', indent: 1, color: 'red' },
        { key: 'acct-ap-invoice-discrepancy', label: 'Invoice Discrepancy', indent: 1, color: 'red' },
        { key: 'acct-ap-chassis-upload', label: 'Chassis Upload', indent: 1, color: 'red' },
        { key: 'acct-ap-per-diem-upload', label: 'Per Diem Upload', indent: 1, color: 'red' },
        { key: 'acct-ap-pending-export-sage', label: 'Pending Export to Sage' },
        { key: 'acct-ap-no-pay-status', label: 'No Pay Status', color: 'red' },
      ],
    }],
  },
  {
    // A section of Accounts Payable in V1; the title keeps that context now that the tile
    // sits in Column A rather than under the AP tile.
    key: 'acct-chassis',
    title: 'Accounts Payable - Chassis',
    columns: [{
      // V1 reads these hold-type rows from cms.additionalcost_holdtypes (tablemaint-editable);
      // the port pins the 7 active types as of 2026-09-09. The card phase should decide
      // whether to derive them from the hold-types endpoint.
      rows: [
        { key: 'acct-chassis-pending-approval', label: 'Pending Approval', color: 'green' },
        { key: 'acct-chassis-on-hold', label: 'On Hold', color: 'red' },
        { key: 'acct-chassis-review', label: 'Review', indent: 1, color: 'red' },
        { key: 'acct-chassis-pending-responsibility', label: 'Pending Responsibility', indent: 1, color: 'red' },
        { key: 'acct-chassis-vendor-dispute', label: 'Vendor Dispute', indent: 1, color: 'red' },
        { key: 'acct-chassis-chassis-upload', label: 'Chassis Upload', indent: 1, color: 'red' },
        { key: 'acct-chassis-pending-export-sage', label: 'Pending Export to Sage' },
        { key: 'acct-chassis-no-pay-status', label: 'No Pay Status', color: 'red' },
      ],
    }],
  },
];

// Starting footprints (24-col grid / 40px rows), sized to the row counts above so a
// fresh tile shows its whole panel. Rows ≈ 29px + tile header ≈ 34px + panel header ≈ 29px
// at 40px grid rows (measured in the browser 2026-09-09).
const COUNT_PANEL_SIZES: Record<string, { w: number; h: number }> = {
  'acct-cms': { w: 6, h: 8 },
  'acct-accessorials': { w: 6, h: 10 },
  'acct-pending-invoicing': { w: 6, h: 8 },
  'acct-invoicing-hold': { w: 6, h: 8 },
  'acct-accessorials-on-hold': { w: 6, h: 8 },
  'acct-approval': { w: 6, h: 8 },
  'acct-accounting': { w: 6, h: 3 },
  'acct-accounts-payable': { w: 6, h: 10 },
  'acct-chassis': { w: 6, h: 10 },
};

/** Fail loudly at import if a count panel has no footprint, instead of a "reading 'w'" crash. */
const sizeOf = (key: string): { w: number; h: number } => {
  const size = COUNT_PANEL_SIZES[key];
  if (!size) throw new Error(`Unknown accounting panel size: ${key}`);
  return size;
};

/** V1's non-count blocks. Placeholders in phase 1; each gets its own card phase. */
export const ACCT_ACTION_PANELS: PanelDef[] = [
  { key: 'acct-total', title: 'Total', defaultW: 6, defaultH: 2 },
  { key: 'acct-sage-exports', title: 'Sage Uploads & Exports', defaultW: 6, defaultH: 7 },
  { key: 'acct-accrual-driver-payroll', title: 'Accrual For Driver Payroll', defaultW: 6, defaultH: 5 },
  { key: 'acct-invoice-search', title: 'Search for Invoice / Master Pro', defaultW: 6, defaultH: 6 },
];

export const ACCT_PANEL_DEFS: PanelDef[] = [
  ...ACCT_COUNT_PANELS.map((p) => {
    const { w, h } = sizeOf(p.key);
    return { key: p.key, title: p.title, defaultW: w, defaultH: h };
  }),
  ...ACCT_ACTION_PANELS,
];

const DEF_BY_KEY = new Map(ACCT_PANEL_DEFS.map((d) => [d.key, d]));

const at = (key: string, x: number, y: number): PanelLayoutItem => {
  const def = DEF_BY_KEY.get(key);
  if (!def) throw new Error(`Unknown accounting panel: ${key}`);
  return { key, x, y, w: def.defaultW, h: def.defaultH, expandedH: def.defaultH };
};

/**
 * Default arrangement for users who have not arranged their own — V1's four columns.
 * Authored at PANEL_GRID_VERSION 2 (24-col grid). Returns fresh objects so the shared
 * default is never mutated.
 *
 * Chassis lives under Accessorials in Column A rather than under Accounts Payable as in
 * V1: with V1's stacking Column C reached 27+ rows while the others stopped near 17.
 * Column heights with the footprints above: A 28 / B 24 / C 23 / D 18.
 */
export const getDefaultPanelLayouts = (): PanelLayouts => ({
  [ACCT_WORKSPACE_ID]: [
    // Column A — orders, accessorials, chassis
    at('acct-cms', 0, 0),
    at('acct-accessorials', 0, 8),
    at('acct-chassis', 0, 18),
    // Column B — pending invoicing and holds
    at('acct-pending-invoicing', 6, 0),
    at('acct-invoicing-hold', 6, 8),
    at('acct-accessorials-on-hold', 6, 16),
    // Column C — approval, past due, payables, total
    at('acct-approval', 12, 0),
    at('acct-accounting', 12, 8),
    at('acct-accounts-payable', 12, 11),
    at('acct-total', 12, 21),
    // Column D — Sage and the two forms
    at('acct-sage-exports', 18, 0),
    at('acct-accrual-driver-payroll', 18, 7),
    at('acct-invoice-search', 18, 12),
  ],
});
